<template>
  <b-container fluid class="p-0 pic-container">
    <!-- Loading durante il caricamento del dettaglio piano -->
    <div
      v-if="loadingDettaglio"
      class="loading-screen"
    >
      <div class="loading-box">
        <b-spinner
          variant="primary"
          label="Caricamento"
        />
        <span>Caricamento dati...</span>
      </div>
    </div>
    <!-- Layout principale del modulo Piani:
       - sidebar sinistra con ricerca e albero
       - dettaglio destra del piano selezionato
    La sidebar può essere compressa per aumentare lo spazio disponibile. -->
    <div ref="layoutRoot" class="pic-layout">
      <!-- SIDEBAR: gestione elenco e navigazione piani -->
      <div class="sidebar-col" :class="{ collapsed: sidebarCollapsed }">
        <b-card no-body class="h-100">
          <!-- Header sempre visibile.
               Quando la sidebar è compressa rimane solo il pulsante per ripristinare la visualizzazione completa.-->
          <div class="panel-header">
              <span>Piano Welfare</span>
              <b-button
                  size="sm"
                  variant="link"
                  class="collapse-btn"
                  @click="sidebarCollapsed = !sidebarCollapsed"
              >
                  <b-icon
                    :icon="sidebarCollapsed ? 'chevron-right' : 'chevron-left'"
                    class="collapse-icon"
                 />
              </b-button>
          </div>
          <!-- Corpo sidebar nascosto quando compressa: contiene ricerca e albero dei piani. -->
          <b-card-body v-show="!sidebarCollapsed" class="p-2">

            <!-- Ricerca piani: filtra l'albero e identifica i nodi corrispondenti. Non apre direttamente il dettaglio.-->
            <b-input-group size="sm" class="mb-3">
              <b-form-input
                v-model="search"
                placeholder="Cerca per ID o descrizione..."
                @input="searchPiano"
                @focus="activeField = 'search'"
                :class="{ 'input-active': activeField === 'search' }"
              />
              <b-input-group-append>
               <b-button
                 class="btn-play"
                 @click="resetSearch"
                  :disabled="!search"
                >
                 ✕
               </b-button>
              </b-input-group-append>
            </b-input-group>

            <!-- Albero gerarchico dei piani. Riceve la struttura filtrata e comunica 
             la selezione del piano tramite evento.-->
            <PICTree
              :nodes="filteredPiani"
              :highlight-ids="searchResultIds"
              :selected-id="selectedPiano ? selectedPiano.id : null"
              @select="treeSelected"
            />
          </b-card-body>
        </b-card>
      </div>

     <!-- AREA DETTAGLIO PIANO SELEZIONATO -->
      <div class="content-col">
        <b-card no-body class="h-100">
          <!--  Header dettaglio fisso.
          Lo scroll viene applicato solamente al contenuto sottostante.-->
          <div class="panel-header">
            Dettaglio Piano
          </div>
          <b-card-body class="detail-body">
            <!--Contenuto scrollabile del dettaglio. 
            Mantiene header e layout principali fissi.-->
            <div class="detail-scroll">
              <div
                v-if="!openedPiano"
                class="text-muted p-3"
              >
                Seleziona un piano dall'elenco di sinistra.
              </div>
              <!-- Componente responsabile della visualizzazione del piano aperto:
                   - dati generali
                   - metriche
                   - distribuzioni
                   - navigazione padre/figlio -->
              <!-- Gestione gerarchia:
                   - parent mantiene il piano padre
                   - selected-child-id indica il figlio aperto permettendo il ritorno al padre. -->
               <!-- metrice: Dati statistici associati al piano corrente -->
               <!-- expanded: Comunica al dettaglio lo spazio disponibile:
                    serve per adattare grafici e componenti interni. -->
                    
              <PICDettaglio
                v-else
                :piano="openedPiano"
                :selected-child-id="selectedChildId"
                :metriche="openedPiano ? openedPiano.metriche || {} : {}"
                :distribuzione="openedPiano ? openedPiano.distribuzione || {} : {}"
                :dipendenti="openedPiano? openedPiano.dipendenti_dettaglio || {}: {}"
                :expanded="sidebarCollapsed"
                @select-child="treeSelected"
              />
            </div>
           </b-card-body>
       </b-card>
      </div>
    </div>
  </b-container>
</template>

<script>

import PICTree from "./PICTree.vue";
import PICDettaglio from "./PICDettaglio.vue";
import picService from "@/services/pic.service";

export default {

  name: "PICPiani",
  components: {
    PICTree,
    PICDettaglio
  },
/* Stato principale del componente.
    Gestisce:
  - ricerca e filtro albero
  - piano selezionato e piano aperto
  - navigazione padre/figlio
  - gestione responsive del layout
  I dati di dettaglio vengono passati a PICDettaglio. */
  data() {
    return {
      //Per attesa caricamento dati
       loadingDettaglio: false,
      // Testo inserito dall'utente per filtrare l'albero dei piani.
      search: "",
      /* Gestione ridimensionamento layout.
         ResizeObserver viene usato perché il cambio dimensione
         può dipendere anche dalla chiusura/apertura sidebar,
         non solo dalla finestra browser. */
      resizeObserver: null,
      resizeTimeout: null,
      sidebarCollapsed: false,
      /* Contiene gli ID dei nodi trovati dalla ricerca.
         Serve solamente per evidenziare i risultati nel tree.*/
      searchResultIds: [],
      /* selectedPiano: elemento selezionato nell'albero.
       openedPiano: elemento mostrato nel dettaglio.
       Sono separati perché la selezione del tree
      e il contenuto visualizzato possono avere comportamenti diversi. */
      selectedPiano: null,
      openedPiano: null,
      /* Mantiene il riferimento al padre quando viene aperto un piano figlio.
         Serve per permettere il ritorno al livello precedente. */
      currentParent: null,
      // figlio selezionato
      selectedChildId: null,
      activeField: null,
      piani: []
    };
  },

  computed: {
    /* Genera una versione filtrata dell'albero.
       L'albero originale non viene modificato:
       viene creata una struttura temporanea
       mantenendo solamente i nodi compatibili con la ricerca. */
    filteredPiani() {

      const text = this.search
        .trim()
        .toLowerCase();

      if (!text)
        return this.piani;

      return this.filterTree(
        this.piani,
        text
      );
    }
  },

  methods: {
    /* Verifica se lo spazio disponibile è sufficiente per mantenere il layout affiancato.
       Comunica al componente padre il cambio modalità. */
    checkLayoutOverflow() {

        const width =
            this.$refs.layoutRoot.clientWidth;

        this.$emit("layout-mode", {
            overflow: width < 681 /*quando il layout sarà definitivo sostituire il valore con un breakpoint calcolato automaticamente dalle dimensioni reali.*/
        });

    },
    /* Evita chiamate continue durante il resize.
       Attende che il ridimensionamento termini prima di ricalcolare il layout. */
    debouncedCheck() {

      clearTimeout(this.resizeTimeout);

      this.resizeTimeout = setTimeout(() => {
        this.checkLayoutOverflow();
      }, 120);
    },
    /* Recupera la struttura gerarchica dei piani dal backend e inizializza l'albero. */
    async loadPiani() {

       this.piani = await picService.getPiani();
    },

    /* Gestione ricerca.
       Aggiorna solamente gli elementi da evidenziare nel tree senza aprire automaticamente il dettaglio.*/
    searchPiano() {

      const text = this.search
        .trim()
        .toLowerCase();

      if (!text) {
        this.searchResultIds = [];
        return;
      }

      this.searchResultIds =
        this.findNodes(
          this.piani,
          text
        );
    },
 
    /* Pulisce lo stato della ricerca e rimuove le evidenziazioni nell'albero. */
    resetSearch() {
      this.search = "";
      this.searchResultIds = [];
    },
    /* Filtra ricorsivamente la struttura ad albero.
       Mantiene:
       - nodi che corrispondono alla ricerca
       - nodi padre necessari per raggiungerli. */
    filterTree(nodes, text) {

      return nodes
        .map(node => {

          const id =
            String(node.id)
              .toLowerCase();

          const nome =
            node.nome
              ? node.nome.toLowerCase()
              : "";

          const match =
            id.includes(text) ||
            nome.includes(text);

          const children =
            node.children
              ? this.filterTree(
                  node.children,
                  text
                )
              : [];

          if (match || children.length) {
            return {
              ...node,
              children,
              expanded: !match && children.length > 0
            };
          }
          return null;
        })
        .filter(Boolean);
    },
  
    /* Punto centrale della navigazione.
       Tutte le selezioni provenienti dall'albero passano da questo metodo.
       Gestisce:
       - apertura dettaglio
       - posizione del piano nella gerarchia;
       - stato corrente della navigazione. */
  
    async treeSelected(piano) {
      this.selectedPiano = piano;
      this.activeField = "selected";

      // Gestione gerarchia
      if (piano.parent) {
        // Il piano è figlio di un altro piano
        this.currentParent = piano.parent;
        this.selectedChildId = piano.id;
      } else {
        // Piano di primo livello
        this.currentParent = piano;
        this.selectedChildId = null;
      }

      this.loadingDettaglio = true;
      try {
        const response = await picService.getDettaglioPiano(piano.id);
       
        // Mantiene parent/children dell'albero
        // e aggiunge/sovrascrive i dati restituiti dall'API
        this.openedPiano = {
          ...piano,
          ...response.data
        };
      } catch (error) {
        console.error(
          "Errore nel caricamento del dettaglio del piano:",
          error
        );

        // Mantiene comunque il piano selezionato
        this.openedPiano = piano;
      } finally {
        this.loadingDettaglio = false;
      }
    },

    /* Ricerca ricorsiva nell'albero.
       Restituisce solo gli ID trovati, utilizzati per evidenziare i nodi. */
    findNodes(nodes, value) {

      let results = [];

      for (const node of nodes) {

        const id =
          String(node.id)
          .toLowerCase();

        const nome =
          node.nome
          ? node.nome.toLowerCase()
          : "";

        if (
          id.includes(value) ||
          nome.includes(value)
        ) {
          results.push(node.id);
        }

        if (node.children) {
          results =
            results.concat(
              this.findNodes(
                node.children,
                value
              )
            );
        }
      }
      return results;
    },

  },
  /* Inizializzazione componente:
      1. carica i piani dal backend
      2. attende il rendering del DOM
      3. attiva il controllo dimensioni layout
     ResizeObserver permette di reagire anche alla compressione della sidebar. */
  async mounted() {

    // Carica i piani dal service
    await this.loadPiani();

    this.$nextTick(() => {

        this.checkLayoutOverflow();

        if (window.ResizeObserver) {

            this.resizeObserver = new ResizeObserver(() => {
                this.debouncedCheck();
            });

            this.resizeObserver.observe(
                this.$refs.layoutRoot
            );
        }
    });
  },
  /* Pulizia risorse.
     Disconnette ResizeObserver e cancella timeout per evitare aggiornamenti su componenti distrutti. */
  beforeDestroy() {

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    clearTimeout(this.resizeTimeout);
  }
};

</script>

<style scoped>



.btn-play {
  background: #373F66;
  border: none;
  color: #fff;
  padding: 4px 10px;
  font-size: 14px;
  line-height: 1;
}

.btn-play:hover {
  background: #2f3658;
}

.btn-play:disabled {
  background: #373F66;
  opacity: .4;
  cursor: not-allowed;
}

/* ---------------- LAYOUT ---------------- */
.pic-container {
  width: 100%;
  height: 100%;
  overflow-x: auto;
  overflow-y: hidden;
}

 .pic-layout {
  display: flex;
  height: 100%;
  gap: 6px;
  min-width: 680px; /*sotto questa misura parte la scroll su tutto PICPiani per schermi (eventualmente mettere varibile dinamica)*/
}

/* ---------------- SIDEBAR ---------------- */
.sidebar-col {
  display: flex;
  flex: 0 1 340px;
  min-width: 300px;
  max-width: 340px;
  transition:
        flex-basis .25s ease,
        min-width .25s ease,
        max-width .25s ease;
}
/* Sidebar compressa.
  Rimane visibile solo il pulsante di riapertura. */
.sidebar-col.collapsed {
    flex-basis:52px;
    min-width:52px;
    max-width:52px;
}

.sidebar-col.collapsed .panel-header {
    height:100%;
    display:flex;
    flex-direction:column;
    justify-content:flex-start;
    align-items:center;
    padding: 0;
}

.sidebar-col.collapsed .panel-header span {
    display:none;
}

.collapse-btn {
  color: #373F66 !important;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  text-decoration: none;
}

.collapse-btn:hover {
  color: #2f3658 !important;
  background: rgba(55,63,102,.08);
  text-decoration: none;
}

.collapse-btn:focus {
  box-shadow: none;
}

.sidebar-col.collapsed .collapse-btn {
    width:48px;
    height:48px;
    margin-top:0;
}

/* ---------------- DETTAGLIO ---------------- */
.content-col {
  flex: 1 1 auto;
  min-width: 0px;
  display: flex;
}
.detail-body {
  flex: 1;
  display: flex;
  overflow: hidden;
  padding: 0;
}
/* Solo il dettaglio è scrollabile.
  Header e layout principale restano fissi. */
.detail-scroll {
    flex: 1;
    overflow: auto;
    padding: 15px;
    background: #f4f6f9;
}

/* ---------------- CARD ---------------- */
.sidebar-col > .card,
.content-col > .card {
  display: flex;
  flex-direction: column;
  flex: 1;
}

/* header card */
.panel-header {
  background: #f8f9fb;
  color: #373F66;
  height: 52px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  font-size: 22px;
  font-weight: 600;
  border: 2px solid #dee2e6;;
 
}
.collapse-icon {
  color: #373F66;
  font-size: 18px;
  stroke: #373F66;
  stroke-width: 1.5;
}

/* corpo card */
.sidebar-col .card-body {
  flex: 1;
  overflow-y: auto;
  border: none;
}

.content-col .card-body {
    flex: 1;
    display: flex;
    overflow: hidden;
    padding: 0;
    border: none;
}

/* ---------------- INPUT ---------------- */
.input-active {
  border-color: #373F66 !important;
}

.input-active:focus {
  border-color: #373F66 !important;
  box-shadow: none !important;
}

/* ---------------- LOADING ---------------- */
.loading-screen {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.25);
  z-index: 9999;
}

.loading-box {
  display: flex;
  align-items: center;
  gap: 10px;

  padding: 14px 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.15);

  font-size: 14px;
  font-weight: 500;
}

</style>